/**
 * DateRangeFilter — chip-pill date filter matching the new dashboard
 * (PageControlBar `DatePill`). Sabeeh 2026-05-12: replaced the older
 * "Quick ranges dropdown + Pick a date range" dual-button shape so the
 * campaign-detail page offers the same six options as the dashboard:
 *
 *   • Last 7 days
 *   • This month       (default for the dashboard, MTD)
 *   • Last month
 *   • This quarter     (QTD ≈ last 90 days)
 *   • Custom range
 *   • All time         (2020-01-01 → today, captures every send)
 *
 * Props are unchanged so existing callers keep working — only the
 * presentation differs.
 */
import React, { useEffect, useRef, useState } from 'react'
import { CalendarIcon } from 'lucide-react'
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { Button as ShButton } from '@/components/ui/button'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent } from '@/components/ui/popover'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '@/lib/utils'

interface DateRange {
  from: Date | undefined
  to: Date | undefined
}

interface DateRangeFilterProps {
  dateRange: DateRange
  onDateRangeChange: (range: DateRange) => void
}

type PresetKey =
  | '7d'
  | 'MTD'
  | 'LAST_MONTH'
  | 'QTD'
  | 'CUSTOM'
  | 'ALL_TIME'

const ALL_TIME_FLOOR = new Date('2020-01-01T00:00:00Z')

function rangeForPreset(key: Exclude<PresetKey, 'CUSTOM'>): DateRange {
  const now = new Date()
  switch (key) {
    case '7d':
      return { from: subDays(now, 6), to: now }
    case 'MTD':
      return { from: startOfMonth(now), to: endOfMonth(now) }
    case 'LAST_MONTH':
      return {
        from: startOfMonth(subMonths(now, 1)),
        to: endOfMonth(subMonths(now, 1)),
      }
    case 'QTD':
      return { from: subDays(now, 90), to: now }
    case 'ALL_TIME':
      return { from: ALL_TIME_FLOOR, to: now }
  }
}

/** Best-effort: tells us which preset (if any) the current range matches.
 * Used to highlight the active chip + the pill label. CUSTOM is the
 * fallback when no preset is exact. */
function detectPreset(r: DateRange): PresetKey {
  if (!r.from || !r.to) return 'ALL_TIME' // page default
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  for (const k of ['7d', 'MTD', 'LAST_MONTH', 'QTD', 'ALL_TIME'] as const) {
    const p = rangeForPreset(k)
    if (sameDay(p.from!, r.from) && sameDay(p.to!, r.to)) return k
  }
  return 'CUSTOM'
}

const PRESET_OPTIONS: Array<{ key: Exclude<PresetKey, 'CUSTOM'>; label: string }> = [
  { key: '7d', label: 'Last 7 days' },
  { key: 'MTD', label: 'This month' },
  { key: 'LAST_MONTH', label: 'Last month' },
  { key: 'QTD', label: 'This quarter' },
  { key: 'ALL_TIME', label: 'All time' },
]

function presetLabel(key: PresetKey, r: DateRange): string {
  switch (key) {
    case '7d':
      return 'Last 7 days'
    case 'MTD':
      return 'This month'
    case 'LAST_MONTH':
      return 'Last month'
    case 'QTD':
      return 'This quarter'
    case 'ALL_TIME':
      return 'All time'
    case 'CUSTOM':
      if (r.from && r.to) {
        return `${format(r.from, 'LLL dd')} – ${format(r.to, 'LLL dd, y')}`
      }
      return 'Custom range'
  }
}

export function DateRangeFilter({
  dateRange,
  onDateRangeChange,
}: DateRangeFilterProps) {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Default the page to "All time" so newly-mounted detail pages show
  // every send (matches what the user expected before the chip rework).
  useEffect(() => {
    if (!dateRange.from && !dateRange.to) {
      onDateRangeChange(rangeForPreset('ALL_TIME'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dropdownOpen) return
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [dropdownOpen])

  const activeKey = detectPreset(dateRange)
  const today = new Date()

  // Calendar popover draft
  const [draft, setDraft] = useState<DateRange>({
    from: dateRange.from,
    to: dateRange.to,
  })
  useEffect(() => {
    if (calendarOpen) {
      setDraft({ from: dateRange.from, to: dateRange.to })
    }
  }, [calendarOpen, dateRange.from, dateRange.to])
  const canApply = !!draft.from && !!draft.to

  const handlePillClick = () => {
    if (activeKey === 'CUSTOM') {
      setCalendarOpen(true)
      setDropdownOpen(false)
    } else {
      setDropdownOpen((v) => !v)
    }
  }

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverPrimitive.Anchor asChild>
          <button
            type="button"
            onClick={handlePillClick}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
              'border-blue-600 bg-blue-600 text-white hover:bg-blue-700',
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {presetLabel(activeKey, dateRange)}
            <span className="opacity-70">▾</span>
          </button>
        </PopoverPrimitive.Anchor>

        <PopoverContent className="p-3 w-auto" align="end" sideOffset={6}>
          {/* Preset chips matching the dashboard's date-pill popover */}
          <div className="flex flex-wrap gap-1 mb-3 pb-3 border-b">
            {PRESET_OPTIONS.map((opt) => {
              const isActive = activeKey === opt.key
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    onDateRangeChange(rangeForPreset(opt.key))
                    setCalendarOpen(false)
                    setDropdownOpen(false)
                  }}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                    isActive
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100',
                  )}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          <CalendarComponent
            mode="range"
            numberOfMonths={2}
            defaultMonth={draft.from ?? subMonths(today, 1)}
            selected={{ from: draft.from, to: draft.to }}
            onSelect={(range) =>
              setDraft({ from: range?.from, to: range?.to })
            }
            disabled={(d) => d > today}
          />
          <div className="text-xs text-muted-foreground text-center mt-2">
            {draft.from && draft.to
              ? `${format(draft.from, 'LLL dd, y')} – ${format(draft.to, 'LLL dd, y')}`
              : draft.from
                ? `${format(draft.from, 'LLL dd, y')} – pick end date`
                : 'Pick a start and end date'}
          </div>
          <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t">
            <ShButton
              variant="outline"
              size="sm"
              onClick={() => setCalendarOpen(false)}
            >
              Cancel
            </ShButton>
            <ShButton
              size="sm"
              disabled={!canApply}
              onClick={() => {
                if (!draft.from || !draft.to) return
                onDateRangeChange({ from: draft.from, to: draft.to })
                setCalendarOpen(false)
              }}
            >
              Apply
            </ShButton>
          </div>
        </PopoverContent>
      </Popover>

      {dropdownOpen && activeKey !== 'CUSTOM' && (
        <div
          className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-slate-200 bg-white shadow-md"
          style={{ minWidth: 160 }}
        >
          {PRESET_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={cn(
                'block w-full text-left px-3 py-2 text-xs hover:bg-slate-100',
                activeKey === opt.key && 'bg-slate-50 font-semibold',
              )}
              onClick={() => {
                onDateRangeChange(rangeForPreset(opt.key))
                setDropdownOpen(false)
              }}
            >
              {opt.label}
            </button>
          ))}
          <div className="border-t border-slate-100" />
          <button
            type="button"
            className="block w-full text-left px-3 py-2 text-xs hover:bg-slate-100"
            onClick={() => {
              setDropdownOpen(false)
              setCalendarOpen(true)
            }}
          >
            Custom range…
          </button>
        </div>
      )}
    </div>
  )
}
